import { Button } from "./ui/button";
import { motion } from "framer-motion";
import { Trophy, Medal, Award, Coins, TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const rankIcons = {
  1: Trophy,
  2: Medal,
  3: Award
};

const rankColors = {
  1: "text-yellow-400 bg-yellow-400/10 border-yellow-400/30",
  2: "text-gray-300 bg-gray-300/10 border-gray-300/30",
  3: "text-amber-600 bg-amber-600/10 border-amber-600/30",
  4: "text-muted-foreground bg-muted border-border"
};

// Colors for each player in the chart
const playerColors = [
  "#FFD700", // Gold
  "#C0C0C0", // Silver
  "#CD7F32", // Bronze
  "#9CA3AF", // Gray
];

export default function GameOverModal({ 
  standings, 
  onClose 
}) {
  if (!standings) return null;

  const playerId = localStorage.getItem("playerId");
  const myRank = standings.find(s => s.player_id === playerId)?.final_rank;

  // Prepare chart data from round_history
  const prepareChartData = () => {
    const allHistories = standings.map(s => s.round_history || []);
    const maxRounds = Math.max(...allHistories.map(h => h.length), 1);
    const xAxisMax = Math.max(10, maxRounds);
    
    const data = [];
    // Start from 0
    const initialPoint = { round: 0 };
    standings.forEach(s => {
      initialPoint[s.player_id] = 0;
    });
    data.push(initialPoint);
    
    // Add each round's data
    for (let i = 0; i < xAxisMax; i++) {
      const point = { round: i + 1 };
      standings.forEach(s => {
        const history = s.round_history || [];
        point[s.player_id] = history[i] !== undefined ? history[i] : null;
      });
      data.push(point);
    }
    
    return data;
  };

  const chartData = prepareChartData();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4" data-testid="game-over-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/90" />
      
      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", duration: 0.5 }}
        className="relative w-full max-w-2xl mx-2 bg-card border border-brass/50 rounded-lg overflow-hidden max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="p-4 md:p-6 text-center bg-gradient-to-b from-brass/10 to-transparent flex-shrink-0">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
          >
            <Trophy className="w-8 h-8 md:w-12 md:h-12 text-brass mx-auto mb-2 md:mb-3" />
          </motion.div>
          <h2 className="font-display text-xl md:text-2xl text-foreground mb-1">
            ゲーム終了
          </h2>
          {myRank === 1 && (
            <p className="text-brass font-display text-sm md:text-base">おめでとう！優勝です！</p>
          )}
        </div>

        {/* Standings */}
        <div className="p-3 md:p-4 space-y-2 flex-1 overflow-y-auto">
          {standings.map((standing, index) => {
            const RankIcon = rankIcons[standing.final_rank];
            const isMe = standing.player_id === playerId;
            
            return (
              <motion.div
                key={standing.player_id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className={`flex items-center justify-between p-2 md:p-3 border rounded-lg ${
                  rankColors[standing.final_rank]
                } ${isMe ? "ring-2 ring-brass" : ""}`}
              >
                <div className="flex items-center gap-2 md:gap-3">
                  <span className="font-display text-lg md:text-xl w-6 md:w-8">
                    #{standing.final_rank}
                  </span>
                  {RankIcon && (
                    <RankIcon className={`w-4 h-4 md:w-5 md:h-5 ${
                      standing.final_rank === 1 ? "text-yellow-400" :
                      standing.final_rank === 2 ? "text-gray-300" :
                      standing.final_rank === 3 ? "text-amber-600" : ""
                    }`} />
                  )}
                  <span className="font-medium text-sm md:text-base truncate max-w-[80px] md:max-w-none">
                    {standing.nickname}
                    {isMe && <span className="text-[10px] md:text-xs text-muted-foreground ml-1">(あなた)</span>}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 font-data text-base md:text-lg">
                    <Coins className="w-3 h-3 md:w-4 md:h-4 text-brass" />
                    <span>{standing.chips}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <TrendingUp className="w-3 h-3 text-blue-400" />
                    <span className="text-blue-400">R{Math.round(standing.apparent || 0)}</span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Chart */}
        <div className="p-2 md:p-4 border-t border-border">
          <h3 className="text-xs md:text-sm text-muted-foreground mb-2 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            みかけレート推移
          </h3>
          <ResponsiveContainer width="100%" height={150}>
            <LineChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis 
                dataKey="round" 
                stroke="#888"
                tick={{ fontSize: 10 }}
                domain={[0, 'dataMax']}
              />
              <YAxis 
                stroke="#888"
                tick={{ fontSize: 10 }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1a1a1a', 
                  border: '1px solid #333',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}
                labelStyle={{ color: '#C5A059' }}
              />
              <Legend 
                wrapperStyle={{ paddingTop: '5px', fontSize: '10px' }}
                formatter={(value) => {
                  const player = standings.find(s => s.player_id === value);
                  return player?.nickname || value;
                }}
              />
              {standings.map((standing, index) => (
                <Line
                  key={standing.player_id}
                  type="monotone"
                  dataKey={standing.player_id}
                  name={standing.player_id}
                  stroke={playerColors[index % playerColors.length]}
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  activeDot={{ r: 4 }}
                  connectNulls={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Footer */}
        <div className="p-3 md:p-4 border-t border-border flex-shrink-0">
          <Button
            onClick={onClose}
            className="w-full bg-brass text-accent-foreground hover:bg-brass/90 font-medium text-sm md:text-base"
          >
            ホームに戻る
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
